/**
 * AgencyReferralCard — surfaces the institution → institution referral programme
 * on the AgencyDashboard.
 *
 * Shows:
 *   • Your unique referral code (copyable + as shareable URL)
 *   • Total referred / total converted counters + credit days earned
 *   • Pending credit days (applied on next verify-license or activate-annual)
 *   • Reward history — one row per referred agency that converted
 *
 * Rewards are +60 days per referred agency that converts to annual. Applied
 * automatically by the backend in `admin_activate_annual`.
 */
import { useEffect, useState } from "react";
import { Gift, Copy, ChevronDown, ChevronUp, Trophy } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";

export default function AgencyReferralCard() {
    const [data, setData] = useState(null);
    const [agency, setAgency] = useState(null);
    const [open, setOpen] = useState(false);
    const [savingOptIn, setSavingOptIn] = useState(false);

    async function reload() {
        try {
            const [ref, me] = await Promise.all([
                api.get("/agency/referrals"),
                api.get("/agency/me"),
            ]);
            setData(ref.data);
            setAgency(me.data.agency);
        } catch { setData(null); }
    }

    useEffect(() => { reload(); }, []);

    if (!data) return null;
    const url = `${window.location.origin}/for-brokers?ref=${encodeURIComponent(data.referral_code || "")}`;
    const converted = data.total_converted || 0;
    const totalRef = data.total_referred || 0;
    const earned = data.credit_days_earned || 0;
    const pending = data.pending_credit_days || 0;
    const optedIn = !!agency?.referral_leaderboard_opt_in;

    const copy = async (val, label) => {
        try { await navigator.clipboard.writeText(val); toast.success(`${label} copied`); }
        catch { toast.error("Copy failed"); }
    };

    const toggleOptIn = async () => {
        setSavingOptIn(true);
        try {
            await api.patch("/agency/referral-opt-in", { opt_in: !optedIn });
            toast.success(!optedIn ? "You're on the public leaderboard" : "Removed from leaderboard");
            await reload();
        } catch (e) {
            toast.error("Couldn't update — try again");
        } finally {
            setSavingOptIn(false);
        }
    };

    return (
        <div className="rounded-xl border border-primary/30 bg-primary/[.04] p-4 md:p-5 space-y-3"
             data-testid="agency-referral-card">
            <div className="flex items-start justify-between gap-3 flex-wrap">
                <div className="flex items-center gap-2">
                    <Gift className="w-5 h-5 text-primary" />
                    <div>
                        <div className="text-sm font-extrabold uppercase tracking-widest">
                            Refer another institution · earn 60 days
                        </div>
                        <div className="text-xs text-muted-foreground mt-0.5">
                            Every school or broker you refer earns you a <b>+60-day extension</b> the moment they
                            activate their annual plan.
                        </div>
                    </div>
                </div>
                <button
                    type="button"
                    onClick={() => setOpen((v) => !v)}
                    className="text-xs font-semibold text-primary inline-flex items-center gap-1 hover:underline"
                    data-testid="agency-referral-toggle"
                >
                    {open ? "Hide details" : "View details"}
                    {open ? <ChevronUp className="w-3 h-3" /> : <ChevronDown className="w-3 h-3" />}
                </button>
            </div>

            <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
                <StatTile label="Referred" value={totalRef} testId="ref-stat-referred" />
                <StatTile label="Converted" value={converted} tone="success" testId="ref-stat-converted" />
                <StatTile label="Days earned" value={earned} tone="primary" testId="ref-stat-earned" />
                <StatTile label="Pending credit" value={pending} tone={pending > 0 ? "warn" : "muted"}
                          testId="ref-stat-pending" />
            </div>

            <div className="rounded-lg border border-border bg-background/70 p-3 text-xs font-mono flex flex-wrap items-center gap-2">
                <span className="text-muted-foreground">Your code</span>
                <span className="font-extrabold tracking-wide text-primary" data-testid="agency-referral-code">
                    {data.referral_code || "—"}
                </span>
                <button
                    type="button"
                    onClick={() => copy(data.referral_code || "", "Referral code")}
                    className="ml-1 text-primary hover:underline inline-flex items-center gap-0.5"
                    data-testid="agency-referral-copy-code"
                ><Copy className="w-3 h-3" /> copy</button>
                <span className="mx-2 text-muted-foreground">·</span>
                <button
                    type="button"
                    onClick={() => copy(url, "Referral link")}
                    className="text-primary hover:underline inline-flex items-center gap-0.5"
                    data-testid="agency-referral-copy-link"
                ><Copy className="w-3 h-3" /> copy signup link</button>
            </div>

            {/* Public leaderboard opt-in — always shown so brokers can opt in
                proactively. The public /public/referral-leaderboard endpoint
                filters to agencies with ≥1 conversion, so opting in early
                doesn't create noise on the marketing site. */}
            <div className="rounded-lg border border-dashed border-primary/40 bg-primary/5 p-3 flex items-start gap-2"
                 data-testid="agency-referral-leaderboard-optin">
                    <Trophy className="w-4 h-4 text-primary shrink-0 mt-0.5" />
                    <div className="flex-1 min-w-0">
                        <div className="text-xs font-extrabold uppercase tracking-widest">
                            Show on the public top-referrers board?
                        </div>
                        <div className="text-[11px] text-muted-foreground mt-0.5">
                            Only your institution name + convert count is published — never trader data, PnL, or seat count.
                            {optedIn && " You're currently listed."}
                        </div>
                    </div>
                    <button
                        type="button"
                        onClick={toggleOptIn}
                        disabled={savingOptIn}
                        data-testid="agency-referral-optin-toggle"
                        className={`shrink-0 text-xs font-bold px-3 py-1.5 rounded transition ${
                            optedIn
                                ? "bg-primary text-primary-foreground"
                                : "border border-primary text-primary hover:bg-primary/10"
                        } disabled:opacity-60`}
                    >
                        {savingOptIn ? "…" : optedIn ? "Opted in ✓" : "Opt in"}
                    </button>
                </div>

            {open && (
                <div className="space-y-2" data-testid="agency-referral-detail">
                    <div className="text-xs font-bold uppercase tracking-widest text-muted-foreground">
                        Institutions you've referred
                    </div>
                    {totalRef === 0 ? (
                        <div className="text-xs text-muted-foreground italic border border-dashed border-border rounded p-3">
                            Nobody yet — share your code with schools or brokers you know. Their first year unlocks
                            your bonus days.
                        </div>
                    ) : (
                        <ul className="space-y-1 text-xs">
                            {data.referred_agencies.map((r) => (
                                <li key={r.agency_id}
                                    className="flex justify-between items-center border border-border rounded px-2 py-1">
                                    <span className="font-semibold">{r.agency_name}</span>
                                    <span className={`text-[10px] uppercase font-bold tracking-widest ${
                                        r.converted_to_annual ? "text-emerald-500"
                                        : r.plan_type === "trial" ? "text-primary"
                                        : "text-muted-foreground"
                                    }`}>
                                        {r.converted_to_annual ? "+60d granted"
                                        : r.plan_type === "trial" ? "on trial"
                                        : r.plan_type}
                                    </span>
                                </li>
                            ))}
                        </ul>
                    )}
                </div>
            )}
        </div>
    );
}

function StatTile({ label, value, tone = "muted", testId }) {
    const cls = tone === "primary" ? "text-primary"
              : tone === "success" ? "text-emerald-500"
              : tone === "warn"    ? "text-amber-500"
              :                      "text-foreground";
    return (
        <div className="rounded border border-border bg-background/60 p-2" data-testid={testId}>
            <div className="text-[10px] uppercase tracking-widest text-muted-foreground">{label}</div>
            <div className={`font-extrabold text-lg ${cls}`}>{value}</div>
        </div>
    );
}
